/**
 * Allowlist sanitizer for `data-slater-bind-html`.
 *
 * Rich-text and embed fields come back from the CMS as raw HTML. Assigning that
 * to `innerHTML` would execute any `<script>`, `<img onerror>`, or
 * `javascript:` URL a CMS editor (or anyone who can write to the collection)
 * put there. Everything not on these lists is unwrapped or dropped.
 *
 * Parsing happens inside an inert document, so `<img src=x onerror=...>` never
 * loads and no script runs even while we inspect the tree.
 */

const ALLOWED_TAGS = new Set([
  "A", "ABBR", "B", "BLOCKQUOTE", "BR", "CAPTION", "CODE", "DD", "DIV", "DL", "DT",
  "EM", "FIGCAPTION", "FIGURE", "H1", "H2", "H3", "H4", "H5", "H6", "HR", "I",
  "IMG", "LI", "OL", "P", "PRE", "S", "SPAN", "STRONG", "SUB", "SUP", "TABLE",
  "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "U", "UL",
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(["href", "title", "target", "rel"]),
  IMG: new Set(["src", "alt", "title", "width", "height", "loading", "srcset", "sizes"]),
};

/** The only schemes that may appear in an `href`/`src`. */
const SAFE_SCHEMES = new Set(["http", "https", "mailto", "tel"]);
/** A leading `scheme:`. A URL without one is relative and cannot execute script. */
const SCHEME = /^([a-z0-9+.-]+):/;
/** Whitespace and C0/C1 control characters, which browsers ignore inside a scheme. */
const IGNORABLE = /[\u0000-\u0020\u007f-\u009f]/g;

/**
 * True when a URL cannot execute script. Shared with `data-slater-bind-attr-*`
 * in render.ts, so attribute binding and rich text enforce ONE scheme policy.
 */
export function isSafeUrl(value: string): boolean {
  // Strip the characters a browser would ignore before looking for a scheme, so
  // `java\tscript:` and `java\0script:` cannot slip past.
  const cleaned = value.replace(IGNORABLE, "").toLowerCase();
  const scheme = SCHEME.exec(cleaned);
  // Relative: `x.jpg`, `/en/blog/x`, `#anchor`, `//cdn/x`.
  if (!scheme) return true;
  return SAFE_SCHEMES.has(scheme[1]!);
}

function scrub(node: Element): void {
  // Iterate over a copy: we mutate the tree as we go.
  for (const child of Array.from(node.children)) scrub(child);

  if (!ALLOWED_TAGS.has(node.tagName)) {
    // Unwrap unknown-but-harmless containers; delete anything that can execute
    // or load. Unwrapping keeps the author's text visible.
    const parent = node.parentNode;
    if (!parent) return;
    if (node.tagName === "SCRIPT" || node.tagName === "STYLE" || node.tagName === "IFRAME") {
      parent.removeChild(node);
      return;
    }
    while (node.firstChild) parent.insertBefore(node.firstChild, node);
    parent.removeChild(node);
    return;
  }

  const allowed = ALLOWED_ATTRS[node.tagName] ?? new Set<string>();
  for (const attr of Array.from(node.attributes)) {
    const name = attr.name.toLowerCase();
    // Drops every `on*` handler, plus anything not explicitly allowed.
    if (!allowed.has(name)) {
      node.removeAttribute(attr.name);
      continue;
    }
    if ((name === "href" || name === "src") && !isSafeUrl(attr.value)) {
      node.removeAttribute(attr.name);
    }
  }

  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
}

export function sanitizeHtml(html: string): string {
  // An inert document: no resource fetches, no script execution.
  const doc = document.implementation.createHTMLDocument("");
  doc.body.innerHTML = html;
  for (const child of Array.from(doc.body.children)) scrub(child);
  return doc.body.innerHTML;
}
